import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import {
  OmsExportRequest,
  OmsProduct,
  OmsReport,
  Stage,
} from "../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import refreshOmsReportMetadata, {
  isStoredOmsReport,
} from "./omsReportMetadata";

export type SavedExport = {
  jobId: string;
  report: OmsReport;
  requestId?: string;
};
type ExportCriteria = Pick<
  OmsExportRequest,
  "filters" | "operation" | "outputColumns"
>;

function readSavedExport(
  product: OmsProduct,
  report: OmsReport,
  mode: OmsExportRequest["mode"],
  contextKey: string
): SavedExport | null {
  try {
    const currentId = sessionStorage.getItem(
      `oms-export:${product}:${report.snapshotId}:${mode}`
    );
    let saved: SavedExport | null = null;
    try {
      saved = JSON.parse(
        sessionStorage.getItem(contextKey) || "null"
      ) as SavedExport | null;
    } catch {
      // A damaged context must not discard a still-readable same-snapshot job ID.
    }
    if (
      saved &&
      typeof saved.jobId === "string" &&
      saved.jobId.length > 0 &&
      isStoredOmsReport(saved.report, product) &&
      (!currentId || currentId === saved.jobId)
    ) {
      return {
        jobId: saved.jobId,
        report: saved.report,
        ...(typeof saved.requestId === "string"
          ? { requestId: saved.requestId }
          : {}),
      };
    }
    return currentId ? { jobId: currentId, report } : null;
  } catch {
    return null;
  }
}

export function restoredOmsExportMode(
  product: OmsProduct,
  report: OmsReport
): "full" | "filtered" {
  try {
    const hasSaved = (mode: "full" | "filtered") =>
      Boolean(
        readSavedExport(
          product,
          report,
          mode,
          `oms-export-latest:${product}:${mode}`
        )
      );
    const active = sessionStorage.getItem(`oms-export-active:${product}`);
    if ((active === "full" || active === "filtered") && hasSaved(active))
      return active;
    if (!hasSaved("full") && hasSaved("filtered")) return "filtered";
  } catch {
    // Manual exports remain usable when session storage is unavailable.
  }
  return "full";
}

export function previousOmsExport(product: OmsProduct, report: OmsReport) {
  const mode = restoredOmsExportMode(product, report);
  const saved = readSavedExport(
    product,
    report,
    mode,
    `oms-export-latest:${product}:${mode}`
  );
  return saved ? { ...saved, mode } : undefined;
}

export default function useOmsExport(
  product: OmsProduct,
  report: OmsReport,
  mode: OmsExportRequest["mode"],
  availableReport?: OmsReport,
  resumeSaved?: boolean,
  retryRequest?: { identity: string; id: string }
) {
  const latestReport = availableReport || report;
  const contextKey = `oms-export-latest:${product}:${mode}`;
  const [saved] = useState(() =>
    resumeSaved !== false
      ? readSavedExport(product, report, mode, contextKey)
      : null
  );
  const [pinnedReport, setSourceReport] = useState(saved?.report || report);
  const sourceReport = useMemo(
    () => refreshOmsReportMetadata(pinnedReport, latestReport),
    [pinnedReport, latestReport]
  );
  const { snapshotId } = sourceReport;
  const storageKey = `oms-export:${product}:${snapshotId}:${mode}`;
  const [jobId, setJobId] = useState<string | null>(saved?.jobId || null);
  const [resumeAvailable, setResumeAvailable] = useState(Boolean(saved));
  const [sent, setSent] = useState(false);
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const request = useRef<{ identity: string; id: string } | null>(
    retryRequest || null
  );
  const [, notifyRequestIdentity] = useState(0);
  function saveAcceptedExport(id: string, rememberMode = false): boolean {
    try {
      const previous = readSavedExport(product, sourceReport, mode, contextKey);
      const requestId =
        request.current?.id ||
        (previous?.jobId === id ? previous.requestId : undefined);
      // Save before updating state: the dialog may close while POST is in flight.
      sessionStorage.setItem(
        contextKey,
        JSON.stringify({
          jobId: id,
          report: sourceReport,
          ...(requestId ? { requestId } : {}),
        })
      );
      sessionStorage.setItem(storageKey, id);
      if (rememberMode)
        sessionStorage.setItem(`oms-export-active:${product}`, mode);
      return true;
    } catch {
      return false;
    }
  }
  function requestBody(criteria: ExportCriteria) {
    return {
      mode,
      filters: criteria.filters || [],
      operation: criteria.operation || "and",
      ...(criteria.outputColumns !== undefined
        ? { outputColumns: criteria.outputColumns }
        : {}),
    };
  }
  const create = useMutation({
    mutationFn: async (criteria: ExportCriteria) => {
      const body = requestBody(criteria);
      const identity = JSON.stringify([product, snapshotId, body]);
      // A lost response must be safe to retry without changing an accepted job.
      if (request.current?.identity !== identity) {
        request.current = { identity, id: uuidv4() };
        notifyRequestIdentity((version) => version + 1);
      }
      return (
        await OmsReportsApiFactory().createOmsExport(
          product,
          Stage.Prod,
          snapshotId,
          { requestId: request.current.id, ...body }
        )
      ).data;
    },
    onSuccess: (job) => {
      setResumeAvailable(saveAcceptedExport(job.jobId, true));
      setJobId(job.jobId);
      setSent(false);
    },
  });
  useEffect(() => {
    if (jobId) setResumeAvailable(saveAcceptedExport(jobId));
    // Persist the source schema together with the accepted job, including old IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, storageKey, contextKey, sourceReport]);
  const job = useQuery({
    queryKey: ["oms-export", product, snapshotId, mode, jobId],
    enabled: Boolean(jobId),
    retry: false,
    queryFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExport(
          product,
          Stage.Prod,
          jobId as string
        )
      ).data,
    refetchInterval: (data, query) =>
      !query.state.error && (!data || data.status === "PREPARING")
        ? 3000
        : false,
    refetchIntervalInBackground: false,
  });
  const download = useMutation({
    mutationFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExportDownload(
          product,
          Stage.Prod,
          jobId as string
        )
      ).data,
    onSuccess: (result) => {
      window.location.assign(result.url);
      setSent(true);
    },
  });
  const expiresAt = job.data?.expiresAt;
  useEffect(() => {
    const deadline = expiresAt ? new Date(expiresAt).getTime() : NaN;
    function checkDeadline() {
      setDeadlinePassed(Boolean(jobId) && Date.now() >= deadline);
    }
    checkDeadline();
    const timer = Number.isFinite(deadline)
      ? window.setTimeout(checkDeadline, Math.max(0, deadline - Date.now()))
      : undefined;
    window.addEventListener("focus", checkDeadline);
    document.addEventListener("visibilitychange", checkDeadline);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", checkDeadline);
      document.removeEventListener("visibilitychange", checkDeadline);
    };
  }, [jobId, expiresAt]);
  function responseStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } } | null)?.response
      ?.status;
  }
  const expired =
    deadlinePassed ||
    responseStatus(job.error) === 410 ||
    responseStatus(download.error) === 410;
  const unavailable =
    responseStatus(job.error) === 404 || responseStatus(download.error) === 404;
  const createConflict = responseStatus(create.error) === 409;
  function changeFilters(nextSource: OmsReport = latestReport) {
    request.current = null;
    setJobId(null);
    setSourceReport(nextSource);
    setResumeAvailable(false);
    setSent(false);
    setDeadlinePassed(false);
    create.reset();
    download.reset();
  }
  function editSettings(nextSource: OmsReport = sourceReport) {
    // Keep the old accepted recovery entry until a successor is accepted.
    changeFilters(nextSource);
  }
  function reset() {
    try {
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(contextKey);
    } catch {
      // Reset still works when browser storage is disabled.
    }
    changeFilters();
  }
  return {
    create,
    job,
    download,
    jobId,
    sourceReport,
    resumeAvailable,
    sent,
    expired,
    unavailable,
    createConflict,
    changeFilters,
    editSettings,
    reset,
    requestIdentity: () => request.current,
    restoreRetryIdentity: (identity?: { identity: string; id: string }) => {
      request.current = identity || null;
    },
    matchesPreviousRequest: (criteria: ExportCriteria) =>
      request.current?.identity ===
      JSON.stringify([product, snapshotId, requestBody(criteria)]),
  };
}
